import type { ProductScreenKey } from '../components/landing/ProductScreenVisual'

export type SalesDeckSlide = {
  id: string
  eyebrow: string
  title: string
  message: string
  bullets: string[]
  visualSuggestion?: string
  visualKey?: ProductScreenKey
  speakerNotes?: string
}

export type SalesDeck = {
  id: string
  slug: string
  title: string
  subtitle: string
  targetBuyer: string
  buyerPainSummary: string
  heroMessage: string
  corePromise: string
  bestUseCase: string
  slides: SalesDeckSlide[]
  cta: SalesDeckSlide
}

const recruitmentAgencySlides: SalesDeckSlide[] = [
  {
    id: 'cover',
    eyebrow: 'Recruitment agencies',
    title: 'Stop being treated like a replaceable supplier.',
    message: 'Become the client workforce operating layer with proof, approvals, and cleaner labor coordination.',
    bullets: [
      'Agency-branded operations for hourly and temporary labor',
      'Scoped client visibility without exposing internal agency data',
      'Geofenced attendance, timesheet approval, and export-ready rows',
    ],
    visualSuggestion: 'Cover visual showing agency, client, supervisor, and worker connected through one operating layer.',
    speakerNotes: 'Open by reframing the agency from labor supplier to operations partner. The promise is not more software; it is stronger client trust and cleaner margin control.',
  },
  {
    id: 'agency-admin-savings',
    eyebrow: 'Admin savings',
    title: 'Give agency coordinators 4-5 hours back every week.',
    message: 'Start with agency operations, then invite client companies into a shared approval and billing workflow that reduces reconciliation before payroll close.',
    bullets: [
      'Reduce workforce administration by 260+ hours per year',
      'Validate schedules, attendance, timesheets, billing checks, and payroll prep from one record',
      'Give clients simple approval visibility without exposing internal agency data',
      'Frame the first rollout as a launch pilot with one client and one worker group',
    ],
    visualSuggestion: 'Weekly admin time stack showing manual validation shrinking into one agency-client approval workflow.',
    speakerNotes: 'Lead with measurable time savings before the category language. The Workforce Network OS explains why the savings scale across clients.',
  },
  {
    id: 'agency-margin-leak',
    eyebrow: 'Margin leak',
    title: 'Every late arrival, no-show, and disputed hour eats agency margin.',
    message: 'Operational mess turns into write-offs, replacement labor, payroll cutoff stress, and client confidence loss.',
    bullets: [
      'Clients ask for proof after the shift has already gone wrong',
      'Coordinators burn time reconciling spreadsheets, chats, and calls',
      'Payroll cutoff becomes a weekly scramble instead of a controlled workflow',
      'Larger competitors look more professional even when service quality is similar',
    ],
    visualSuggestion: 'Before/after margin stack showing disputes, admin time, replacements, and write-offs shrinking under NexusWorkforce360.',
    speakerNotes: 'Keep the conversation commercial. The problem is not just admin burden; it is margin erosion and replaceability.',
  },
  {
    id: 'whatsapp-spreadsheets-break',
    eyebrow: 'Old way',
    title: 'WhatsApp and spreadsheets work until the client asks for accountability.',
    message: 'Chat threads coordinate the moment, but they do not create a reliable operating record.',
    bullets: [
      'Worker lists drift across files and client-specific templates',
      'Timesheets arrive late, incomplete, or in formats payroll must clean',
      'No-show disputes depend on screenshots and memory',
      'Shared workers across clients are hard to track with confidence',
    ],
    visualSuggestion: 'Split screen of scattered chats and spreadsheet tabs beside a single assignment and attendance record.',
    speakerNotes: 'Acknowledge that chat is useful. The issue is using it as the system of record.',
  },
  {
    id: 'client-wants-confidence',
    eyebrow: 'Buyer truth',
    title: 'The client does not want your spreadsheet. They want confidence.',
    message: 'Clients need enough visibility to trust attendance, hours, and approvals without entering the agency back office.',
    bullets: [
      'Who was assigned to the client job or shift',
      'Who actually attended, where, and under which exception rules',
      'Which hours are ready for supervisor or company approval',
      'Which worker documents or certificates are ready when required',
    ],
    visualSuggestion: 'Client-facing confidence checklist with scoped visibility labels.',
    speakerNotes: 'This is the transition to scoped access: show the client what builds trust, not everything the agency knows.',
  },
  {
    id: 'agency-command-center',
    eyebrow: 'NexusWorkforce360 solution',
    title: 'NexusWorkforce360 becomes the agency command center.',
    message: 'Run rosters, assignments, attendance proof, approvals, reliability, and client visibility from one workforce network process.',
    bullets: [
      'Agency, client company, supervisor, and worker workflows in one platform',
      'Organization relationships and sharing agreements with scoped access',
      'Assignment history that connects worker, client, job, location, and approval path',
      'Export-ready operational rows for payroll and billing review',
    ],
    visualSuggestion: 'Command center wireframe with roster, assignments, attendance, approvals, and exports.',
    speakerNotes: 'Use “command center” to make the platform practical, not abstract. It is where daily operations get controlled.',
  },
  {
    id: 'roster-skills-documents',
    eyebrow: 'Roster readiness',
    title: 'Know who can work before you promise coverage.',
    message: 'Worker roster data, skills, documents, and reliability signals help agencies assign with fewer surprises.',
    bullets: [
      'Maintain worker records across client assignments',
      'Track skills, documents, certificates, and expiry risk',
      'See reliability indicators from attendance and assignment history',
      'Reduce last-minute replacement decisions based only on coordinator memory',
    ],
    visualSuggestion: 'Worker profile cards with readiness, document, and reliability indicators.',
    speakerNotes: 'Avoid overclaiming matching automation. Position this as operational decision support from real records.',
  },
  {
    id: 'assignment-workflow',
    eyebrow: 'Workflow',
    title: 'Assign workers to the right client, job, shift, and approval path.',
    message: 'NexusWorkforce360 connects demand from client jobs or orders to worker assignments and supervisor review.',
    bullets: [
      'Create job/order staffing workflows by client and location',
      'Assign workers to shifts with clear ownership and context',
      'Support open shifts and worker applications where the operation uses them',
      'Keep assignment history tied to attendance and timesheets',
    ],
    visualSuggestion: 'Flow diagram: client order to worker assignment to punch to approval to export.',
    speakerNotes: 'This slide should feel demo-ready: show the buyer the order of operations they can recognize.',
  },
  {
    id: 'attendance-proof',
    eyebrow: 'Proof',
    title: 'Your timesheet is not proof. It is a claim.',
    message: 'Geofenced punches, offline exception review, and audit context turn attendance into evidence.',
    bullets: [
      'Punch in and out against approved work locations',
      'Flag or review offline punches and location exceptions',
      'Give supervisors clearer evidence before approving hours',
      'Reduce late/no-show and attendance disputes with a shared record',
    ],
    visualSuggestion: 'Map pin with punch timeline and exception review panel.',
    speakerNotes: 'This is one of the strongest lines in the deck. Let it land, then show how NexusWorkforce360 creates defensible context.',
  },
  {
    id: 'approval-payroll-export',
    eyebrow: 'Close the week',
    title: 'Payroll cutoff should be a workflow, not a rescue mission.',
    message: 'Move from worked shifts to approved timesheets and export-ready rows with less reconciliation.',
    bullets: [
      'Supervisor and company approval states are attached to timesheet records',
      'Adjustments, disputes, and comments stay connected to the work record',
      'Payroll and billing teams can filter by client, worker, job, date, and status',
      'Export-ready data reduces manual cleanup before invoicing or payroll',
    ],
    visualSuggestion: 'Approval queue flowing into a payroll/export table.',
    speakerNotes: 'Do not promise direct payroll sync. Say export-ready and integration-ready when asked about downstream systems.',
  },
  {
    id: 'client-portal',
    eyebrow: 'Scoped visibility',
    title: 'Show clients the truth they need, not the agency data they should not see.',
    message: 'NexusWorkforce360 supports client visibility through organization relationships and access boundaries.',
    bullets: [
      'Clients can review relevant workers, assignments, attendance, and approvals',
      'Agency-owned operational details stay protected by scoped access',
      'Sharing agreements clarify which organization can see and act on what',
      'Audit trails support a professional client operating rhythm',
    ],
    visualSuggestion: 'Two-pane access model showing agency internal view and client scoped view.',
    speakerNotes: 'This is a differentiator for smaller agencies that need professionalism without giving away control.',
  },
  {
    id: 'harder-to-replace',
    eyebrow: 'Competitive position',
    title: 'Stop selling hours. Sell operational control.',
    message: 'When the agency owns the operating layer, the relationship becomes harder to replace.',
    bullets: [
      'Clients see proof, approvals, and reporting instead of chasing coordinators',
      'Agency teams look more organized during disputes and renewals',
      'Reliability and compliance records improve repeat staffing decisions',
      'Operational data becomes part of the client value proposition',
    ],
    visualSuggestion: 'Value ladder from supplier to preferred partner to workforce operating layer.',
    speakerNotes: 'Close the narrative loop: the agency becomes sticky because it reduces client operational risk.',
  },
]

const constructionSlides: SalesDeckSlide[] = [
  {
    id: 'cover',
    eyebrow: 'Construction companies',
    title: 'Know who is on site, who sent them, and what hours are approvable.',
    message: 'NexusWorkforce360 gives construction teams a workforce control layer across contractors, suppliers, sites, documents, attendance, and approvals.',
    bullets: [
      'Site and project visibility across companies and labor sources',
      'Geofenced attendance with supervisor review',
      'Compliance, timesheets, and export-ready reporting by site and supplier',
    ],
    visualSuggestion: 'Construction site map with suppliers, supervisors, and worker status grouped by zone.',
    speakerNotes: 'Position construction labor as a supply chain problem. Buyers understand subcontractor complexity immediately.',
  },
  {
    id: 'construction-admin-savings',
    eyebrow: 'Admin savings',
    title: 'Take 260+ hours/year out of site labor administration.',
    message: 'Construction teams and labor suppliers can validate attendance, approvals, invoice questions, and payroll-ready hours from the same site record.',
    bullets: [
      'Save 4-5 admin hours per week on timesheet and billing validation',
      'Tie agency/subcontractor assignments to geofenced attendance and supervisor approval',
      'Reduce invoice disputes with shared evidence before month-end',
      'Track skills, document readiness, and progression paths without claiming external training integrations',
    ],
    visualSuggestion: 'Site workflow showing supplier assignment, punch evidence, supervisor approval, invoice validation, and export.',
    speakerNotes: 'Make the buyer feel the admin relief first, then explain the construction labor supply chain model.',
  },
  {
    id: 'labor-supply-chain',
    eyebrow: 'Problem',
    title: 'Construction labor is a supply chain, not a spreadsheet.',
    message: 'Every site depends on direct workers, subcontractors, agencies, supervisors, documents, approvals, and commercial terms moving together.',
    bullets: [
      'Multiple suppliers send workers into the same delivery environment',
      'Site teams need coverage while procurement needs supplier performance',
      'Compliance status can change before the next shift',
      'Weak operating records turn into invoice and payroll disputes',
    ],
    visualSuggestion: 'Supply chain map from supplier to site zone to supervisor approval.',
    speakerNotes: 'Use this to move the buyer away from “time app” thinking and toward workforce supply chain control.',
  },
  {
    id: 'not-knowing-cost',
    eyebrow: 'Risk',
    title: 'The real cost is not knowing until after the shift.',
    message: 'When attendance and eligibility are unclear, delays, rework, disputes, and safety exposure show up after damage is done.',
    bullets: [
      'Manual gate or timekeeper processes miss important context',
      'No-shows and late arrivals ripple into project delivery',
      'Timesheets get approved after the fact with weak evidence',
      'Site supervisors lack a clean mobile view of who is working',
    ],
    visualSuggestion: 'Risk timeline from planned crew to late discovery to disputed invoice.',
    speakerNotes: 'Anchor on cost of uncertainty, not only cost of admin.',
  },
  {
    id: 'supplier-coordination',
    eyebrow: 'Coordination',
    title: 'Every subcontractor adds another version of the truth.',
    message: 'NexusWorkforce360 standardizes how companies, agencies, and subcontractors coordinate work without flattening their ownership boundaries.',
    bullets: [
      'Model agencies, companies, and subcontractors as organizations',
      'Connect supplier relationships to sites, jobs, and assignments',
      'Give each participant the access needed for their role',
      'Preserve tenant and organization boundaries for cleaner accountability',
    ],
    visualSuggestion: 'Organization relationship diagram for owner, general contractor, subcontractor, agency, and supervisor.',
    speakerNotes: 'This explains why multi-organization architecture matters in buyer language.',
  },
  {
    id: 'compliance-before-assignment',
    eyebrow: 'Compliance',
    title: 'Compliance should be checked before assignment, not during a dispute.',
    message: 'Document and certificate readiness can be part of the operating record before workers arrive on site.',
    bullets: [
      'Track IDs, permits, inductions, trade certificates, and expiry risk',
      'Attach eligibility context to worker and assignment records',
      'Support supervisor review with clearer readiness signals',
      'Reduce manual document chasing across folders and email',
    ],
    visualSuggestion: 'Worker eligibility panel with document status and expiry indicators.',
    speakerNotes: 'Do not claim automated legal compliance decisions. Say readiness checks and review context.',
  },
  {
    id: 'geofence-supervisor',
    eyebrow: 'Site visibility',
    title: 'Geofenced attendance gives supervisors a cleaner field record.',
    message: 'Punch proof, location rules, breaks, and exceptions help teams decide which hours are approvable.',
    bullets: [
      'Site and location geofences define where attendance can be recorded',
      'Supervisor views show who is expected, present, late, or missing where available',
      'Offline punch exceptions can be reviewed before approval',
      'Break and punch history supports stronger timekeeping discipline',
    ],
    visualSuggestion: 'Supervisor mobile view with site list, punch status, and exception badge.',
    speakerNotes: 'This is the field workflow slide. Keep it practical for site supervisors.',
  },
  {
    id: 'site-org-tree',
    eyebrow: 'Operating model',
    title: 'Run labor by project, site, zone, company, and supplier.',
    message: 'NexusWorkforce360 supports a hierarchy that mirrors how construction work is actually managed.',
    bullets: [
      'Organize work by tenant, organization, project, site, and location',
      'Map job/order staffing to suppliers and site supervisors',
      'Filter reporting by site, agency, company, job, assignment, and date',
      'Keep operational accountability attached to the right layer',
    ],
    visualSuggestion: 'Project tree with site, zone, supplier, worker, and approval nodes.',
    speakerNotes: 'Tie this to implementation credibility: the platform understands the shape of the business.',
  },
  {
    id: 'shift-control',
    eyebrow: 'Workflow',
    title: 'Control the shift before it becomes an invoice line.',
    message: 'Assignment, attendance, approval, and export are connected instead of reconstructed at month end.',
    bullets: [
      'Create job/order staffing needs by supplier or work package',
      'Assign workers to approved locations and shift windows',
      'Review attendance evidence and exceptions before sign-off',
      'Move approved hours into export-ready reporting',
    ],
    visualSuggestion: 'Workflow strip: order, assignment, geofence punch, supervisor approval, export.',
    speakerNotes: 'Use this as the demo path for a construction buyer.',
  },
  {
    id: 'invoice-defense',
    eyebrow: 'Commercial control',
    title: 'Timesheet approval is invoice defense.',
    message: 'Approved hours should be backed by attendance evidence, supervisor review, and a clean audit trail.',
    bullets: [
      'Reduce invoice disputes with clearer work records',
      'Attach comments, adjustments, disputes, and approvals to timesheets',
      'Give finance and procurement cleaner data for supplier conversations',
      'Export rows by site, company, supplier, job, and approval state',
    ],
    visualSuggestion: 'Invoice line linked back to approved shift evidence.',
    speakerNotes: 'This resonates with procurement and finance. It connects field data to commercial confidence.',
  },
  {
    id: 'supplier-performance',
    eyebrow: 'Supplier performance',
    title: 'Supplier performance should be visible before renewal.',
    message: 'Attendance, reliability, approval exceptions, and compliance readiness create a better supplier management conversation.',
    bullets: [
      'Compare reliability by supplier, site, job, or worker group',
      'Identify late arrivals, no-shows, and recurring exception patterns',
      'Support procurement with operational evidence',
      'Use data to improve staffing plans, not just settle disputes',
    ],
    visualSuggestion: 'Supplier scorecard with attendance, reliability, document readiness, and approval metrics.',
    speakerNotes: 'Avoid overclaiming predictive analytics. The value is structured operational evidence.',
  },
  {
    id: 'gcc-demo-story',
    eyebrow: 'Demo story',
    title: 'A GCC construction pilot can start with one site and one approval path.',
    message: 'The platform can be demonstrated through a focused site workflow before a larger rollout.',
    bullets: [
      'Start with one project site and a small supplier category',
      'Load worker roster, documents, geofence, and shift assignments',
      'Review punches, exceptions, supervisor approvals, and export rows',
      'Use findings to define supplier rollout and reporting requirements',
    ],
    visualSuggestion: 'Pilot storyboard from site setup to first export review.',
    speakerNotes: 'Keep the pilot low-friction and operationally credible.',
  },
]

const foodBeverageSlides: SalesDeckSlide[] = [
  {
    id: 'cover',
    eyebrow: 'Food and beverage',
    title: 'Control hourly staffing across branches, shifts, absences, and rush periods.',
    message: 'NexusWorkforce360 helps F&B operators manage coverage, attendance, approvals, and payroll-ready data without drowning managers in spreadsheets.',
    bullets: [
      'Branch and shift staffing visibility',
      'Worker availability, open shifts, and mobile punch workflows',
      'Manager approvals and export-ready timesheet data',
    ],
    visualSuggestion: 'Multi-branch staffing board with shift coverage and attendance status.',
    speakerNotes: 'Frame this around manager relief and operational consistency across locations.',
  },
  {
    id: 'food-beverage-admin-savings',
    eyebrow: 'Admin savings',
    title: 'Save 4-5 admin hours weekly across branch approvals and payroll cleanup.',
    message: 'F&B operators can reduce manager follow-up, late timesheets, invoice questions, and payroll prep by keeping branch schedules, attendance, approvals, and exports in one workflow.',
    bullets: [
      '260+ hours/year back to operations and branch managers',
      'Validate who was scheduled, who attended, who approved, and what is export-ready',
      'Support skills visibility and progression for cross-trained hourly staff',
      'Start with two branches, then expand after approval and payroll readiness are proven',
    ],
    visualSuggestion: 'Two-branch admin savings board with coverage, manager approval, billing/payroll validation, and export readiness.',
    speakerNotes: 'Keep it concrete for busy operators: fewer follow-ups, cleaner approvals, less payroll cleanup.',
  },
  {
    id: 'missed-shift',
    eyebrow: 'Pain',
    title: 'Every missed shift becomes an operations problem.',
    message: 'A no-show is not just an attendance event; it affects service speed, overtime, customer experience, and manager focus.',
    bullets: [
      'Last-minute gaps hit peak service windows hardest',
      'Branch managers coordinate by chat instead of a shared workflow',
      'Overtime and attendance uncertainty show up in payroll',
      'Flexible and part-time workers need a simple mobile experience',
    ],
    visualSuggestion: 'Rush-period coverage heat map showing a missing worker and overtime risk.',
    speakerNotes: 'Make this feel like the reality of restaurants, cafes, catering, hotels, and cloud kitchens.',
  },
  {
    id: 'chat-scheduling-fails',
    eyebrow: 'Old way',
    title: 'Chat-based scheduling fails when branches need the same workers.',
    message: 'Informal coordination cannot reliably show availability, coverage gaps, attendance proof, and approvals across locations.',
    bullets: [
      'Workers move between branches without clean assignment history',
      'Managers maintain separate spreadsheets or chat lists',
      'Shift swaps and absences are hard to reconcile later',
      'Payroll teams inherit unclear hours and late manager approvals',
    ],
    visualSuggestion: 'Chat thread turning into branch schedule conflicts beside a clean NexusWorkforce360 shift board.',
    speakerNotes: 'Do not dismiss chat; explain why it should not be the staffing record.',
  },
  {
    id: 'branch-control',
    eyebrow: 'NexusWorkforce360 solution',
    title: 'Give every branch the same staffing control process.',
    message: 'NexusWorkforce360 organizes hourly labor by branch, location, shift, worker, manager, and approval state.',
    bullets: [
      'Branch/location-based scheduling and assignments',
      'Geofence rules for attendance by approved workplace',
      'Manager views for expected, present, late, and missing workers where available',
      'Multi-location dashboards for operational leaders',
    ],
    visualSuggestion: 'Branch operations dashboard with locations, shift fill rate, and approval queue.',
    speakerNotes: 'This is the core platform slide for F&B: local manager workflow plus regional visibility.',
  },
  {
    id: 'availability-open-shifts',
    eyebrow: 'Coverage',
    title: 'Turn availability and open shifts into a controlled workflow.',
    message: 'Flexible labor works better when managers can see who is available, who applied, and who was assigned.',
    bullets: [
      'Track worker availability and preferred work patterns',
      'Publish open shifts where the operation uses application workflows',
      'Assign approved workers to branch shifts with clear context',
      'Use reliability history to support repeat staffing decisions',
    ],
    visualSuggestion: 'Open shift card with applicants, availability, reliability, and assignment action.',
    speakerNotes: 'Avoid claiming AI matching. Emphasize visibility and controlled assignment decisions.',
  },
  {
    id: 'attendance-breaks',
    eyebrow: 'Attendance',
    title: 'Know who worked, where, and whether breaks were recorded.',
    message: 'Punches, location rules, breaks, and exceptions make timekeeping clearer before payroll review.',
    bullets: [
      'Punch in and out with branch or workplace location rules',
      'Track breaks and attendance exceptions',
      'Review offline punches before approving hours',
      'Reduce payroll uncertainty from handwritten or late timesheets',
    ],
    visualSuggestion: 'Worker mobile punch flow beside manager exception review.',
    speakerNotes: 'For F&B, keep mobile simplicity central. Managers and workers are busy.',
  },
  {
    id: 'manager-approvals',
    eyebrow: 'Approval',
    title: 'Manager approvals should happen before payroll finds the problem.',
    message: 'Approval queues turn shift records into reviewed timesheets with comments, adjustments, and audit history.',
    bullets: [
      'Branch managers approve or flag hours by shift',
      'Comments and adjustments stay attached to timesheet records',
      'Payroll teams can filter by branch, worker, status, and date',
      'Export-ready rows reduce manual cleanup',
    ],
    visualSuggestion: 'Approval queue with branch filters and export-ready status.',
    speakerNotes: 'This is the bridge from operations to finance.',
  },
  {
    id: 'multi-branch',
    eyebrow: 'Visibility',
    title: 'Regional leaders need more than one branch manager at a time.',
    message: 'Multi-branch visibility helps leaders see coverage, attendance, exceptions, and approval progress across locations.',
    bullets: [
      'Compare branch staffing status and attendance exceptions',
      'See where manager approvals are complete or delayed',
      'Track workers who support multiple locations',
      'Identify repeat coverage gaps before they become normal',
    ],
    visualSuggestion: 'Multi-branch map or grid with exception and approval badges.',
    speakerNotes: 'This is where multi-location operators, franchises, and hotel groups should lean in.',
  },
  {
    id: 'reliability-repeat-staffing',
    eyebrow: 'Repeat staffing',
    title: 'The best staffing plan remembers who actually showed up.',
    message: 'Reliability signals from attendance and assignment history help managers reuse stronger workers and spot risk earlier.',
    bullets: [
      'Track punctuality, completed assignments, and attendance patterns',
      'Support repeat staffing for reliable workers across branches',
      'Reduce dependence on informal manager memory',
      'Give operators a cleaner view of flexible labor performance',
    ],
    visualSuggestion: 'Worker reliability panel grouped by branch and role.',
    speakerNotes: 'Keep this operational. The score supports judgment; it does not replace manager decisions.',
  },
  {
    id: 'integration-ready',
    eyebrow: 'Reporting',
    title: 'Payroll and POS tie-ins need clean operational data first.',
    message: 'NexusWorkforce360 creates export-ready staffing, attendance, approval, and timesheet rows designed for downstream integrations.',
    bullets: [
      'Export data by branch, worker, shift, date, and approval state',
      'Prepare cleaner inputs for payroll review',
      'Keep POS/payroll/API integration language future-safe and integration-ready',
      'Use one operational source before connecting downstream systems',
    ],
    visualSuggestion: 'Export table feeding optional payroll, POS, or API tie-in blocks.',
    speakerNotes: 'Be explicit that this is integration-ready, not a claim of completed POS or payroll sync.',
  },
  {
    id: 'pilot',
    eyebrow: '30-day pilot',
    title: 'A 30-day F&B pilot should prove coverage, approvals, and payroll readiness.',
    message: 'Start with a contained branch group and worker pool, then measure staffing control before expanding.',
    bullets: [
      'Launch with two branches and about twenty workers',
      'Configure branch locations, shift templates, and manager approvals',
      'Review attendance exceptions, coverage gaps, and export rows weekly',
      'Use pilot data to decide rollout by branch cluster',
    ],
    visualSuggestion: '30-day pilot calendar with setup, first shifts, review, and rollout decision.',
    speakerNotes: 'This makes the CTA feel achievable for busy operators.',
  },
]

const securitySlides: SalesDeckSlide[] = [
  {
    id: 'cover',
    eyebrow: 'Security companies',
    title: 'Prove guard presence, control post coverage, and reduce client disputes.',
    message: 'NexusWorkforce360 gives security operators auditable shift operations by site, post, guard, supervisor, approval, and export state.',
    bullets: [
      'Site and post-based guard assignments',
      'Geofenced punch proof and exception review',
      'Scoped client reporting without exposing internal agency data',
    ],
    visualSuggestion: 'Security site map with guard posts, punch status, and supervisor oversight.',
    speakerNotes: 'Security buyers sell trust. The platform proof point is auditable presence.',
  },
  {
    id: 'security-admin-savings',
    eyebrow: 'Admin savings',
    title: 'Save 4-5 admin hours weekly on guard timesheet and billing validation.',
    message: 'Security teams can invite clients into scoped coverage approvals while agency operations keep one source of truth for posts, attendance, disputes, billing, and payroll exports.',
    bullets: [
      'Reduce workforce administration by 260+ hours per year',
      'Connect guard assignment, geofenced presence, supervisor review, client approval, and export',
      'Reduce billing disputes with client-ready records before invoices are challenged',
      'Track guard skills, license/document readiness, reliability, and advancement paths',
    ],
    visualSuggestion: 'Guard post workflow from assignment to punch proof to client approval and billing/payroll export.',
    speakerNotes: 'Security buyers care about trust and billing defense. Lead with the administrative hours saved and the shared validation workflow.',
  },
  {
    id: 'trust-disputed-timesheets',
    eyebrow: 'Pain',
    title: 'Security is sold on trust, but disputed with timesheets.',
    message: 'When a client questions coverage, a manual timesheet rarely settles the issue on its own.',
    bullets: [
      'Clients want proof guards were on site',
      'Late arrivals and missed posts create immediate escalation',
      'Manual timesheets are challenged during billing review',
      'Supervisors need visibility by site and shift',
    ],
    visualSuggestion: 'Disputed timesheet contrasted with punch evidence and post coverage record.',
    speakerNotes: 'This opening should feel sharp and familiar to guard company owners.',
  },
  {
    id: 'coverage-proof',
    eyebrow: 'Coverage proof',
    title: 'The guard coverage proof problem is bigger than payroll.',
    message: 'Coverage proof affects client trust, contract renewal, incident response, billing, and supervisor accountability.',
    bullets: [
      'Guards rotate between client sites and posts',
      'Post orders and shift coverage are hard to track manually',
      'Missed patrols or late arrivals can become client disputes',
      'License or document readiness may matter by client or site',
    ],
    visualSuggestion: 'Client site board with posts, guards, coverage gaps, and document readiness indicators.',
    speakerNotes: 'Bring in license and readiness carefully. The platform tracks documents; it does not guarantee regulatory decisions.',
  },
  {
    id: 'site-post-ops',
    eyebrow: 'NexusWorkforce360 solution',
    title: 'Run security operations by site, post, guard, and shift.',
    message: 'NexusWorkforce360 connects guard assignments, geofenced attendance, supervisor visibility, approvals, and client-facing reports.',
    bullets: [
      'Create site and post structures for client locations',
      'Assign guards to shifts with clear supervisor context',
      'Review punch proof and offline exceptions',
      'Move approved time into export-ready billing and payroll rows',
    ],
    visualSuggestion: 'Operations view with site, post, assignment, punch, approval, and export columns.',
    speakerNotes: 'This slide is the “what it does” anchor for the demo.',
  },
  {
    id: 'assign-guards',
    eyebrow: 'Workflow',
    title: 'Assign guards to posts before coverage becomes a client question.',
    message: 'Clear assignments establish who should be where, when, and under which approval path.',
    bullets: [
      'Plan guard coverage by client site, post, and shift',
      'Keep assignment history as guards rotate between locations',
      'Support supervisor review by site or client account',
      'Use reliability and document readiness when making repeat assignments',
    ],
    visualSuggestion: 'Guard assignment board grouped by client site and post.',
    speakerNotes: 'Tie assignment planning directly to later proof and billing confidence.',
  },
  {
    id: 'geofenced-proof',
    eyebrow: 'Attendance proof',
    title: 'Presence proof should be tied to the post, not a message thread.',
    message: 'Geofenced punches and exception review create a more defensible attendance record.',
    bullets: [
      'Punch in and out against approved site or post geofences',
      'Review offline punch exceptions before approval',
      'Track late, missing, or exception activity by guard and site',
      'Give clients clearer reporting when coverage is challenged',
    ],
    visualSuggestion: 'Post geofence with punch timeline and exception status.',
    speakerNotes: 'This is the proof/audit slide. Keep it concrete and evidence-based.',
  },
  {
    id: 'supervisor-view',
    eyebrow: 'Supervisor control',
    title: 'Supervisors need a site view, not another phone call.',
    message: 'Scoped views help supervisors understand coverage status and focus on exceptions.',
    bullets: [
      'See assigned, present, late, missing, and exception status where available',
      'Review attendance and timesheet records by client site',
      'Escalate issues with cleaner operational context',
      'Approve or flag hours before billing and payroll close',
    ],
    visualSuggestion: 'Supervisor dashboard with site filters and guard status cards.',
    speakerNotes: 'Supervisors are often the buyer’s operational bottleneck. Give them a cleaner daily view.',
  },
  {
    id: 'client-reporting',
    eyebrow: 'Scoped reporting',
    title: 'Clients need reporting, not your internal operating data.',
    message: 'NexusWorkforce360 supports client-facing visibility through scoped access and reports that protect agency operations.',
    bullets: [
      'Share relevant coverage, attendance, and approval status',
      'Keep internal staffing, worker, and commercial details protected',
      'Use organization relationships to control access boundaries',
      'Reduce dispute cycles with auditable client-ready records',
    ],
    visualSuggestion: 'Client report preview beside protected internal operations view.',
    speakerNotes: 'This is a strong agency value point: professional transparency without oversharing.',
  },
  {
    id: 'reliability-license',
    eyebrow: 'Readiness',
    title: 'Guard reliability and license readiness belong in the same operating record.',
    message: 'Attendance history, completion patterns, and document status support stronger guard assignment decisions.',
    bullets: [
      'Track reliability indicators from real shift history',
      'Maintain document and license readiness where required',
      'Spot repeat late arrivals, missed shifts, or exception patterns',
      'Support better repeat staffing for high-trust client sites',
    ],
    visualSuggestion: 'Guard readiness card with reliability and document/license status.',
    speakerNotes: 'Avoid regulatory overclaiming. This is readiness tracking and operational review.',
  },
  {
    id: 'billing-payroll-confidence',
    eyebrow: 'Export confidence',
    title: 'Billing and payroll should point to the same approved truth.',
    message: 'Approved shifts, punch context, comments, and export-ready rows reduce manual disputes at close.',
    bullets: [
      'Filter hours by client, site, post, guard, date, and approval status',
      'Keep adjustments and disputes attached to the timesheet record',
      'Create cleaner billing and payroll exports',
      'Use audit history to defend approved hours',
    ],
    visualSuggestion: 'Approved guard hours flowing into billing and payroll export tables.',
    speakerNotes: 'This makes the back-office value explicit.',
  },
  {
    id: 'beats-chat-spreadsheets',
    eyebrow: 'Before vs after',
    title: 'This beats WhatsApp plus spreadsheets because the record survives the shift.',
    message: 'NexusWorkforce360 turns coordination into a durable operating record the client, supervisor, and back office can trust.',
    bullets: [
      'Before: scattered messages, manual timesheets, unclear coverage proof',
      'After: assigned guards, geofenced punches, reviewed exceptions, approved hours',
      'Before: client disputes rely on screenshots and memory',
      'After: client-ready records support renewal and billing conversations',
    ],
    visualSuggestion: 'Before/after panel showing chat fragments versus a complete shift record.',
    speakerNotes: 'This is the story slide before the pilot close.',
  },
]

const industryDeck = (input: {
  id: string
  slug: string
  title: string
  segment: string
  workflow: string
  roi: string
  compliance: string
  pilot: string
}): SalesDeck => ({
  id: input.id,
  slug: input.slug,
  title: input.title,
  subtitle: 'Industry-specific Workforce Network OS positioning.',
  targetBuyer: input.segment,
  buyerPainSummary: `${input.segment} need a reliable way to coordinate people, sites, shifts, compliance, attendance proof, approvals, and payroll readiness without duplicating operating systems.`,
  heroMessage: input.workflow,
  corePromise: 'One operating layer for workforce demand, supply, assignment coverage, compliance, execution proof, and settlement readiness.',
  bestUseCase: input.pilot,
  slides: [
    {
      id: 'problem',
      eyebrow: 'Problem',
      title: 'The old way scatters accountability.',
      message: 'Spreadsheets, chat threads, and disconnected time records make coverage, compliance, and payroll close harder than the work itself.',
      bullets: ['Coverage status is not visible until someone asks', 'Attendance proof arrives after disputes begin', 'Approvals and payroll readiness depend on manual cleanup'],
      visualSuggestion: 'Old-way workflow with separate roster, schedule, chat, timecard, and payroll files.',
    },
    {
      id: 'platform-model',
      eyebrow: 'Workforce Network OS',
      title: 'NexusWorkforce360 keeps the operating record connected.',
      message: 'The same tenant-scoped product model adapts terminology and dashboard emphasis without duplicating core workflow logic.',
      bullets: ['Organizations model agencies, companies, and partners', 'Assignments connect workers, sites, shifts, attendance, and approvals', 'Permissions and tenant boundaries remain the source of truth'],
      visualSuggestion: 'Network OS diagram connecting organizations, workers, jobs, sites, attendance, approvals, and exports.',
    },
    {
      id: 'industry-workflow',
      eyebrow: 'Industry workflow',
      title: input.workflow,
      message: 'The deck language, dashboard cards, and setup guidance match the buyer’s operating vocabulary while preserving the shared platform foundation.',
      bullets: ['Industry-specific labels and route content', 'Preset dashboard card titles', 'Setup steps that emphasize the relevant first workflow'],
      visualSuggestion: 'Industry dashboard mockup showing preset cards and terminology.',
    },
    {
      id: 'roi',
      eyebrow: 'ROI',
      title: input.roi,
      message: 'The first commercial proof point is less coordination waste before payroll, billing, or client review.',
      bullets: ['Reduce manual status chasing', 'Shorten approval cleanup cycles', 'Improve dispute readiness with shared evidence'],
      visualSuggestion: 'Before/after admin workload and approval-cycle chart.',
    },
    {
      id: 'proof',
      eyebrow: 'Compliance and proof',
      title: input.compliance,
      message: 'Attendance proof, document context, approvals, and audit trails make weekly operations easier to defend.',
      bullets: ['Location-aware attendance where configured', 'Credential or document visibility where relevant', 'Approval history tied to the work record'],
      visualSuggestion: 'Timeline from assignment to punch proof to approval and export.',
    },
    {
      id: 'implementation',
      eyebrow: 'Implementation path',
      title: input.pilot,
      message: 'Start narrow, validate the workflow, and expand across sites, clients, or workforce groups after the first operating cycle.',
      bullets: ['Pick one active workforce group', 'Load locations, workers, jobs/shifts, and approval users', 'Compare old workflow effort against the connected record'],
      visualSuggestion: 'Pilot rollout checklist with scope, setup, first cycle, review, and expansion.',
    },
  ],
  cta: {
    id: 'cta',
    eyebrow: 'CTA',
    title: 'Pilot the operating layer before broad rollout.',
    message: 'Use one contained workflow to prove coverage visibility, attendance proof, approval readiness, and payroll/export cleanup.',
    bullets: ['Choose the first segment and site/client/facility', 'Run one scheduling and approval cycle', 'Review measurable admin savings and dispute reduction'],
    visualSuggestion: 'Pilot CTA slide with target workflow and success measures.',
  },
})

// Shared commercial appendix appended to every deck so any segment conversation
// can move from operational value into pricing, ROI, the partner/commission
// opportunity, and a concrete rollout plan without leaving the deck.
const commercialSlides: SalesDeckSlide[] = [
  {
    id: 'pricing',
    eyebrow: 'Pricing',
    title: 'Run your workforce for free. Pay for acceleration.',
    message:
      'Core workforce operations have no per-worker or per-location price. Customers pay for faster processing, automation, advanced integrations, intelligence, and enterprise services when they need them.',
    bullets: [
      'Free: $0 base access for core workforce operations with standard processing priority',
      'Acceleration: configurable paid priority, faster matching, higher concurrency, and deeper automation',
      'Add-ons: Market Demand, advanced reporting, integrations, AI, compliance, communications, and services',
      'Enterprise / Self-hosted: SSO, SLA, deployment options, data residency, procurement, and dedicated support',
      'No per-worker or per-location pricing for core workforce operations; fair-use and security protections remain',
      'Commercial catalog products cover modules, usage packs, integrations, support, implementation, and future marketplace apps',
      'Initial packs include Operations, Learning, Agency Network, Compliance, Communications, Integrations, Platform / API, and Services & Support',
    ],
    visualKey: 'sales-pipeline',
    visualSuggestion: 'Use the Sales Workspace pricing and lead context beside the public pricing table so the buyer sees pricing tied to actual CRM follow-up.',
    speakerNotes:
      'Anchor on Free core operations and the buyer’s desired processing speed, automation depth, integrations, and services—not workforce capacity. One-time implementation, data migration, training, custom integration, and support fees remain separate commercial terms.',
  },
  {
    id: 'roi',
    eyebrow: 'ROI',
    title: 'The platform pays for itself in reclaimed admin hours.',
    message:
      'Replacing manual validation, billing checks, approval chasing, and payroll prep returns 4-5 admin hours per week — about 260+ hours per year — back to operations.',
    bullets: [
      'Save 4-5 admin hours every week on hours, billing, and payroll validation',
      'Reduce invoice disputes with shared attendance and approval evidence',
      'Fewer payroll corrections and faster month-end close',
      'Example: ~260 hours/year reclaimed is operational value before choosing optional acceleration or advanced extras',
    ],
    visualKey: 'timesheet-approvals',
    visualSuggestion: 'Show Timesheets approval rows where disputed hours, supervisor approval, and payroll readiness replace manual weekly admin cleanup.',
    speakerNotes: 'Use the public ROI calculator live if the buyer wants to plug in their own worker count and admin rate.',
  },
  {
    id: 'agency-vs-company',
    eyebrow: 'Who buys',
    title: 'One platform, two buyer stories: agency and company.',
    message:
      'Agencies win client trust and protect margin; companies gain workforce transparency and cleaner approvals. The same operating record serves both sides of the relationship.',
    bullets: [
      'Agency pitch: become the client workforce operating layer, prove coverage, and reduce billing disputes',
      'Agency pitch: scoped client visibility without exposing internal agency data or commercial terms',
      'Company pitch: see who is on site, who sent them, and which hours are approvable across suppliers',
      'Company pitch: standardize approvals, compliance readiness, and payroll-export data across locations',
    ],
    visualKey: 'network-relationships',
    visualSuggestion: 'Show Network Relationships with agency-to-company sharing, granted locations, and scoped visibility over the shared operating record.',
    speakerNotes: 'Pick the column that matches the room, but show both to signal the network effect.',
  },
  {
    id: 'partner-commission',
    eyebrow: 'Partner program',
    title: 'Sales reps and referral partners earn recurring commission.',
    message:
      'Referral links and sales-rep attribution are built into the platform. Commissions are calculated from real paid invoices, not promises, and tracked in the Sales workspace.',
    bullets: [
      'Earn 25% of paid invoice revenue for the first 18 months, then 10% ongoing',
      'Example: a $4,350 paid invoice at 25% pays $1,087.50 in commission',
      'Referral links, conversion tracking, and payout statements are managed in-app',
      'Attribution is captured at signup; payouts settle from verified paid invoices',
    ],
    visualKey: 'sales-pipeline',
    visualSuggestion: 'Show Sales Workspace with lead pipeline, referral conversion, paid-invoice attribution, and pending commission status.',
    speakerNotes: 'Use this when the buyer is also a potential channel partner or reseller.',
  },
  {
    id: 'implementation-roadmap',
    eyebrow: 'Implementation roadmap',
    title: 'From kickoff to first payroll close in four phases.',
    message:
      'A focused rollout proves value in one operating cycle before scaling across clients, sites, or workforce groups.',
    bullets: [
      'Phase 1 — Kickoff: configure tenant, locations, geofences, and admin users',
      'Phase 2 — Load: import worker roster, documents, jobs, and shift assignments',
      'Phase 3 — Run: live punches, supervisor approvals, and exception review for one cycle',
      'Phase 4 — Settle & expand: payroll/billing export, measure savings, then widen rollout',
    ],
    visualKey: 'payroll-export',
    visualSuggestion: 'Show Payroll Export after the first operating cycle, with ready rows, export formats, billing checks, and settlement review.',
    speakerNotes: 'Tie each phase to the demo checklist so the buyer can see exactly what the first weeks look like.',
  },
]

const withCommercialSlides = (deck: SalesDeck): SalesDeck => ({
  ...deck,
  slides: [...deck.slides, ...commercialSlides],
})

const baseSalesDecks = {
  general: industryDeck({
    id: 'general',
    slug: 'general',
    title: 'NexusWorkforce360 for General Workforce Operations',
    segment: 'Operations leaders coordinating people, sites, shifts, compliance, and payroll.',
    workflow: 'Coordinate workforce demand, supply, coverage, execution, and settlement from one operating record.',
    roi: 'Reduce weekly coordination and payroll cleanup across locations.',
    compliance: 'Keep attendance, approvals, and compliance context close to the work.',
    pilot: 'Pilot with one location, one supervisor group, and one approval/export workflow.',
  }),
  staffing: industryDeck({
    id: 'staffing',
    slug: 'staffing',
    title: 'NexusWorkforce360 for Staffing Agencies',
    segment: 'Staffing, manpower, and recruitment agencies supplying hourly or shift-based labor.',
    workflow: 'Move from client orders to worker availability, assignments, client approvals, and payroll/billing readiness.',
    roi: 'Improve fill visibility and reduce timesheet reconciliation across clients.',
    compliance: 'Use worker documents, attendance proof, and approval history to reduce client disputes.',
    pilot: 'Pilot with one client, one recurring order type, and one worker group.',
  }),
  healthcare: industryDeck({
    id: 'healthcare',
    slug: 'healthcare',
    title: 'NexusWorkforce360 for Healthcare Workforce Operations',
    segment: 'Healthcare providers and staffing teams managing nurses, staff, facilities, and units.',
    workflow: 'Track staffing requests, coverage gaps, credential expirations, staff attendance, and approval readiness.',
    roi: 'Reduce coordinator time spent chasing coverage and credential status.',
    compliance: 'Bring credential visibility and attendance proof into the approval workflow.',
    pilot: 'Pilot with one facility or unit and one staffing request workflow.',
  }),
  homeCare: industryDeck({
    id: 'homeCare',
    slug: 'home-care',
    title: 'NexusWorkforce360 for Home Care Agencies',
    segment: 'Home care agencies coordinating caregivers, client homes, visits, and credentials.',
    workflow: 'Manage scheduled visits, missed or late visits, caregiver availability, client coverage, and payroll readiness.',
    roi: 'Reduce missed-visit follow-up and weekly payroll cleanup.',
    compliance: 'Connect caregiver credentials, visit attendance, and approval evidence.',
    pilot: 'Pilot with one care coordinator, one caregiver group, and one recurring visit workflow.',
  }),
  hospitality: industryDeck({
    id: 'hospitality',
    slug: 'hospitality',
    title: 'NexusWorkforce360 for Hospitality and Events',
    segment: 'Hotels, venues, catering teams, and event staffing operators.',
    workflow: 'Coordinate event coverage, open shifts, staff availability, venue activity, and approval readiness.',
    roi: 'Reduce event staffing scramble and post-event timesheet cleanup.',
    compliance: 'Attach attendance proof and approvals to event and venue work records.',
    pilot: 'Pilot with one venue or event series and one manager approval workflow.',
  }),
  propertyServices: industryDeck({
    id: 'propertyServices',
    slug: 'property-services',
    title: 'NexusWorkforce360 for Property Services',
    segment: 'Property maintenance, cleaning, janitorial, and facilities service teams.',
    workflow: 'Track service order coverage, site attendance, proof of service, missed visits, and billing/payroll readiness.',
    roi: 'Reduce proof-of-service chasing and recurring-site billing disputes.',
    compliance: 'Tie site attendance and service proof to approvals and exports.',
    pilot: 'Pilot with one property group and one recurring service workflow.',
  }),
  recruitmentAgencies: {
    id: 'recruitmentAgencies',
    slug: 'recruitment-agencies',
    title: 'NexusWorkforce360 for Recruitment Agencies',
    subtitle: 'Become the client workforce operating layer.',
    targetBuyer: 'Small to medium staffing and recruitment agencies supplying hourly, temp, contract, security, hospitality, warehouse, construction, or blue-collar labor.',
    buyerPainSummary: 'Agencies lose margin and client confidence when attendance proof, shared workers, timesheets, documents, and payroll cutoff workflows live in spreadsheets and chat threads.',
    heroMessage: 'Stop being treated like a replaceable supplier. Become the client workforce operating layer.',
    corePromise: 'Protect margin and client trust with scoped visibility, attendance proof, approvals, and export-ready data.',
    bestUseCase: 'Pilot with one client, one worker group, and one weekly approval/export workflow.',
    slides: recruitmentAgencySlides,
    cta: {
      id: 'pilot-one-client',
      eyebrow: 'Pilot CTA',
      title: 'Run a pilot with one client and one worker group.',
      message: 'Prove the operating layer in a contained workflow before expanding across clients.',
      bullets: [
        'Select one active client with recurring hourly labor',
        'Load the roster, client location, assignments, and approval users',
        'Run geofenced punches and exception review for one payroll cycle',
        'Compare dispute rate, approval speed, and export readiness against the old process',
      ],
      visualSuggestion: 'Pilot plan with client, roster, geofence, approvals, export review, and expansion decision.',
      speakerNotes: 'End with a low-risk pilot. The ask is not a platform-wide transformation on day one.',
    },
  },
  constructionCompanies: {
    id: 'constructionCompanies',
    slug: 'construction',
    title: 'NexusWorkforce360 for Construction Companies',
    subtitle: 'Control site labor across companies, suppliers, compliance, attendance, and approvals.',
    targetBuyer: 'Construction companies, project owners, general contractors, subcontractors, project managers, procurement teams, and site supervisors.',
    buyerPainSummary: 'Construction teams struggle to know who is on site, who sent them, whether documents are ready, which hours are defensible, and how suppliers are performing.',
    heroMessage: 'Know who is on site, who sent them, whether they are compliant, and what hours are approvable.',
    corePromise: 'Give project, site, supervisor, finance, and procurement teams one operating record for labor control.',
    bestUseCase: 'Pilot on one site, one supplier category, and one supervisor approval workflow.',
    slides: constructionSlides,
    cta: {
      id: 'pilot-one-site',
      eyebrow: 'Pilot CTA',
      title: 'Pilot on one site, one supplier category, one approval workflow.',
      message: 'Prove construction labor control with a focused site rollout.',
      bullets: [
        'Choose a site with active supplier or subcontractor labor',
        'Configure site geofence, worker roster, documents, jobs, and assignments',
        'Run supervisor approval and export review for selected shifts',
        'Use evidence to refine rollout by supplier, site, or project phase',
      ],
      visualSuggestion: 'Site pilot map with supplier category, supervisor approval, and export review milestones.',
      speakerNotes: 'Close with a practical construction pilot that can be measured quickly.',
    },
  },
  foodAndBeverage: {
    id: 'foodAndBeverage',
    slug: 'food-beverage',
    title: 'NexusWorkforce360 for Food & Beverage Operators',
    subtitle: 'Control branch staffing, attendance, approvals, and payroll-ready data.',
    targetBuyer: 'Restaurants, cafes, cloud kitchens, catering operators, franchises, hotels with F&B departments, and multi-branch operators.',
    buyerPainSummary: 'F&B operators face no-shows, branch-level chat coordination, shift coverage gaps, overtime uncertainty, workers moving between branches, manual timesheets, and payroll errors.',
    heroMessage: 'Control hourly staffing across branches, shifts, absences, and rush periods without drowning managers in spreadsheets.',
    corePromise: 'Standardize branch staffing and attendance control while keeping the worker mobile experience simple.',
    bestUseCase: 'Start with two branches, twenty workers, manager approvals, and payroll/export review.',
    slides: foodBeverageSlides,
    cta: {
      id: 'pilot-two-branches',
      eyebrow: 'Pilot CTA',
      title: 'Start with two branches and twenty workers.',
      message: 'Use a small branch cluster to prove coverage visibility, attendance quality, manager approval speed, and payroll readiness.',
      bullets: [
        'Configure branch locations, worker roster, and shift patterns',
        'Use open shifts or direct assignments based on the current workflow',
        'Run punch, break, exception, and manager approval review',
        'Export approved rows and compare payroll cleanup effort',
      ],
      visualSuggestion: 'Two-branch pilot board with shifts, punches, approvals, and export readiness.',
      speakerNotes: 'F&B pilots need to be lightweight. Anchor the close on two branches and measurable operating relief.',
    },
  },
  securityCompanies: {
    id: 'securityCompanies',
    slug: 'security',
    title: 'NexusWorkforce360 for Security Companies',
    subtitle: 'Prove guard presence and reduce client disputes with auditable shift operations.',
    targetBuyer: 'Security guard companies, facility security providers, event security coordinators, and property management security teams.',
    buyerPainSummary: 'Security operators need to prove guard presence, manage post coverage, track rotating guards, review exceptions, protect internal data, and produce billing/payroll-ready records.',
    heroMessage: 'Prove guard presence, control post coverage, and reduce client disputes with auditable shift operations.',
    corePromise: 'Turn guard coverage into a defensible operating record by site, post, shift, approval, and export state.',
    bestUseCase: 'Pilot with three sites, one supervisor, geofenced punch proof, and client-ready reports.',
    slides: securitySlides,
    cta: {
      id: 'pilot-three-sites',
      eyebrow: 'Pilot CTA',
      title: 'Pilot with three sites and one supervisor.',
      message: 'Prove post coverage and client-ready reporting without exposing internal operating data.',
      bullets: [
        'Configure three client sites with post or location geofences',
        'Assign guards, supervisor access, and approval responsibilities',
        'Review punch proof, offline exceptions, and approved hours',
        'Share scoped coverage reporting with the client sponsor',
      ],
      visualSuggestion: 'Three-site pilot plan with supervisor review and client report output.',
      speakerNotes: 'The pilot should directly test the security buyer’s trust promise: presence proof and fewer disputes.',
    },
  },
} satisfies Record<string, SalesDeck>

export const salesDecks = Object.fromEntries(
  Object.entries(baseSalesDecks).map(([key, deck]) => [key, withCommercialSlides(deck)]),
) as Record<keyof typeof baseSalesDecks, SalesDeck>

export type SalesDeckKey = keyof typeof salesDecks

export const salesDeckList = Object.values(salesDecks)

export const salesDeckBySlug = new Map(salesDeckList.map((deck) => [deck.slug, deck]))
